import { ROUTES_MAPPING } from '@/constants/routes.mapping';
import { resolveMarketingUrl } from '@/constants/routeMapper';

/**
 * A single breadcrumb crumb. `url` is always absolute so the same shape can feed
 * both the visible breadcrumb trail and the schema.org `BreadcrumbList` JSON-LD
 * (Google requires the two to agree).
 */
export type BreadcrumbCrumb = {
  name: string;
  url: string;
};

/** Structural parent segments that are not real pages and must not become links. */
const NON_PAGE_SEGMENTS = new Set([
  'page',
  'blog',
  'article',
  'post',
  'articles',
  'blogs',
]);

/** Segments whose marketing label differs from a naive title-case of the slug. */
const SEGMENT_LABELS: Record<string, string> = {
  specialities: 'Specialities',
  speciality: 'Speciality',
  'our-specialists': 'Our Specialists',
  'news-and-events': 'News & Events',
  'health-blogs': 'Health Blogs',
  blogs: 'Blogs',
  videos: 'Videos',
  about: 'About',
  'about-lips': 'About LIPS Healthcare',
  'for-patients': 'For Patients',
  'for-clinicians': 'For Clinicians',
  'our-policies': 'Our Policies',
  'gp-membership': 'GP Membership',
  'private-gp': 'Private GP',
};

function titleCaseSegment(segment: string): string {
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Resolves the display label for a breadcrumb segment, preferring the site's own
 * route mapping (single source of truth for human-readable page titles) and
 * falling back to a title-cased slug for routes that are not in the mapping.
 */
function resolveSegmentLabel(segment: string): string {
  const overridden = SEGMENT_LABELS[segment.toLowerCase()];
  if (overridden) return overridden;

  const match = ROUTES_MAPPING.find(
    (entry) => entry.marketingUrl.toLowerCase() === `/${segment.toLowerCase()}`,
  );
  if (match) return match.label;

  return titleCaseSegment(segment);
}

function stripQueryAndHash(path: string): string {
  return path.split('?')[0].split('#')[0];
}

/**
 * Builds a breadcrumb trail for a path, deriving it from the same route mapping
 * that drives `src/app/sitemap.ts`.
 *
 * Behaviour:
 * - Root path yields a single "Home" crumb.
 * - The trail is canonicalised through `resolveMarketingUrl`, so an
 *   implementation path such as `/about-us` produces the marketing path
 *   `/about-lips` exactly as the sitemap emits it.
 * - Non-page structural segments (e.g. pagination `/blogs/page/2`) are skipped
 *   rather than linked, so we never emit a crumb that would 404.
 *
 * Safe to call from both server and client components: it is pure and imports
 * only static route data.
 */
export function buildBreadcrumbTrail(path: string): BreadcrumbCrumb[] {
  const cleaned = stripQueryAndHash(path);
  const canonical = resolveMarketingUrl(cleaned) ?? cleaned;
  const normalized = canonical.replace(/^\/+|\/+$/g, '');

  const crumbs: BreadcrumbCrumb[] = [{ name: 'Home', url: '/' }];
  if (!normalized) return crumbs;

  const segments = normalized.split('/').filter(Boolean);
  let accumulated = '';

  for (const segment of segments) {
    // Structural segments (e.g. pagination `/blogs/page/2`) are not pages and
    // nothing after them can be linked safely either - the accumulator would
    // otherwise produce URLs like `/blogs/2` that 404. Stop at the last real page.
    if (NON_PAGE_SEGMENTS.has(segment.toLowerCase())) break;

    accumulated += `/${segment}`;
    crumbs.push({ name: resolveSegmentLabel(segment), url: accumulated });
  }

  return crumbs;
}
